import { Head, Link, useForm } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

interface QuizQuestion {
  question: string
  options: string[]
  correct_answer: number
}

interface TrainingVideo {
  id: number
  title: string
  description: string
  video_url: string
  duration_minutes: number
  category: string
  sort_order: number
  is_required: boolean
  module_type: 'video' | 'text' | 'quiz'
  passing_score: number
  content_data: {
    pages?: string[]
    questions?: QuizQuestion[]
  }
}

interface Props extends PageProps {
  video: TrainingVideo | null
  module_types: string[]
}

export default function TrainingForm({ video }: Props) {
  const isEdit = !!video

  const { data, setData, post, put, processing } = useForm({
    title: video?.title || '',
    description: video?.description || '',
    video_url: video?.video_url || '',
    duration_minutes: video?.duration_minutes || 10,
    category: video?.category || '',
    sort_order: video?.sort_order || 0,
    is_required: video?.is_required || false,
    module_type: video?.module_type || 'video',
    passing_score: video?.passing_score || 80,
    content_data: video?.content_data || {}
  })

  const [textPages, setTextPages] = useState<string[]>(
    video?.content_data?.pages || ['']
  )
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>(
    video?.content_data?.questions || [{ question: '', options: ['', ''], correct_answer: 0 }]
  )

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const contentData: Record<string, unknown> = {}
    if (data.module_type === 'text') {
      contentData.pages = textPages.filter(p => p.trim())
    } else if (data.module_type === 'quiz') {
      contentData.questions = quizQuestions.filter(q => q.question.trim())
    }

    setData('content_data', contentData)

    if (isEdit) {
      put(`/training_videos/${video.id}`)
    } else {
      post('/training_videos')
    }
  }

  const addTextPage = () => {
    setTextPages([...textPages, ''])
  }

  const updateTextPage = (index: number, content: string) => {
    const newPages = [...textPages]
    newPages[index] = content
    setTextPages(newPages)
  }

  const removeTextPage = (index: number) => {
    if (textPages.length > 1) {
      setTextPages(textPages.filter((_, i) => i !== index))
    }
  }

  const addQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, { question: '', options: ['', ''], correct_answer: 0 }])
  }

  const updateQuestion = (index: number, field: string, value: any) => {
    const newQuestions = [...quizQuestions]
    newQuestions[index] = { ...newQuestions[index], [field]: value }
    setQuizQuestions(newQuestions)
  }

  const addOption = (qIndex: number) => {
    const newQuestions = [...quizQuestions]
    newQuestions[qIndex].options.push('')
    setQuizQuestions(newQuestions)
  }

  const updateOption = (qIndex: number, oIndex: number, value: string) => {
    const newQuestions = [...quizQuestions]
    newQuestions[qIndex].options[oIndex] = value
    setQuizQuestions(newQuestions)
  }

  const removeOption = (qIndex: number, oIndex: number) => {
    const newQuestions = [...quizQuestions]
    if (newQuestions[qIndex].options.length > 2) {
      newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== oIndex)
      if (newQuestions[qIndex].correct_answer >= newQuestions[qIndex].options.length) {
        newQuestions[qIndex].correct_answer = 0
      }
      setQuizQuestions(newQuestions)
    }
  }

  const removeQuestion = (index: number) => {
    if (quizQuestions.length > 1) {
      setQuizQuestions(quizQuestions.filter((_, i) => i !== index))
    }
  }

  return (
    <AppLayout>
      <Head title={isEdit ? 'Edit Training Module' : 'New Training Module'} />

      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/training" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit Training Module' : 'New Training Module'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card space-y-4">
            <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">
              Basic Information
            </h2>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                Title *
              </label>
              <input
                type="text"
                value={data.title}
                onChange={e => setData('title', e.target.value)}
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                Description
              </label>
              <textarea
                value={data.description}
                onChange={e => setData('description', e.target.value)}
                className="input-field w-full"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Module Type *
                </label>
                <select
                  value={data.module_type}
                  onChange={e => setData('module_type', e.target.value as any)}
                  className="input-field w-full"
                >
                  <option value="video">Video</option>
                  <option value="text">Reading / Text Pages</option>
                  <option value="quiz">Quiz</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={data.category}
                  onChange={e => setData('category', e.target.value)}
                  className="input-field w-full"
                  placeholder="e.g., Onboarding, Compliance"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={data.duration_minutes}
                  onChange={e => setData('duration_minutes', parseInt(e.target.value) || 0)}
                  className="input-field w-full"
                  min={0}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={data.sort_order}
                  onChange={e => setData('sort_order', parseInt(e.target.value) || 0)}
                  className="input-field w-full"
                />
              </div>

              {data.module_type === 'quiz' && (
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                    Passing Score (%)
                  </label>
                  <input
                    type="number"
                    value={data.passing_score}
                    onChange={e => setData('passing_score', parseInt(e.target.value) || 80)}
                    className="input-field w-full"
                    min={0}
                    max={100}
                  />
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.is_required}
                onChange={e => setData('is_required', e.target.checked)}
                className="w-4 h-4 rounded border-chestnut-gray text-chestnut-forest focus:ring-chestnut-forest"
              />
              <span className="text-sm text-chestnut-charcoal">
                Required for agent activation
              </span>
            </label>
          </div>

          {data.module_type === 'video' && (
            <div className="card space-y-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">
                Video Content
              </h2>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Video URL *
                </label>
                <input
                  type="url"
                  value={data.video_url}
                  onChange={e => setData('video_url', e.target.value)}
                  className="input-field w-full"
                  placeholder="https://www.youtube.com/embed/..."
                />
                <p className="text-xs text-chestnut-charcoal/60 mt-1">
                  Use an embed URL for YouTube/Vimeo or a direct video file URL
                </p>
              </div>
            </div>
          )}

          {data.module_type === 'text' && (
            <div className="card space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">
                  Text Pages
                </h2>
                <button type="button" onClick={addTextPage} className="btn-secondary flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Page
                </button>
              </div>

              {textPages.map((page, index) => (
                <div key={index} className="border border-chestnut-gray rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-chestnut-charcoal">
                      Page {index + 1}
                    </span>
                    {textPages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTextPage(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={page}
                    onChange={e => updateTextPage(index, e.target.value)}
                    className="input-field w-full"
                    rows={6}
                    placeholder="Enter page content (HTML supported)"
                  />
                </div>
              ))}
            </div>
          )}

          {data.module_type === 'quiz' && (
            <div className="card space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">
                  Quiz Questions
                </h2>
                <button type="button" onClick={addQuizQuestion} className="btn-secondary flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Question
                </button>
              </div>

              {quizQuestions.map((q, qIndex) => (
                <div key={qIndex} className="border border-chestnut-gray rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-chestnut-charcoal">
                      Question {qIndex + 1}
                    </span>
                    {quizQuestions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeQuestion(qIndex)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <input
                    type="text"
                    value={q.question}
                    onChange={e => updateQuestion(qIndex, 'question', e.target.value)}
                    className="input-field w-full"
                    placeholder="Enter question"
                  />

                  <div className="space-y-2">
                    <label className="text-sm text-chestnut-charcoal/60">
                      Options (select the correct answer)
                    </label>
                    {q.options.map((option, oIndex) => (
                      <div key={oIndex} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct-${qIndex}`}
                          checked={q.correct_answer === oIndex}
                          onChange={() => updateQuestion(qIndex, 'correct_answer', oIndex)}
                          className="w-4 h-4 text-chestnut-forest"
                        />
                        <input
                          type="text"
                          value={option}
                          onChange={e => updateOption(qIndex, oIndex, e.target.value)}
                          className="input-field flex-1"
                          placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                        />
                        {q.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(qIndex, oIndex)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addOption(qIndex)}
                      className="text-sm text-chestnut-forest hover:underline"
                    >
                      + Add option
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end gap-4">
            <Link href="/training" className="btn-secondary">
              Cancel
            </Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : isEdit ? 'Update Module' : 'Create Module'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
